import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Op } from 'quill/core';
import React, { useState } from 'react';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import ScrollArea from '../../components/ScrollArea';
import Tooltip from '../../components/Tooltip';
import { useQuill } from '../../editor/QuillContext';
import type { Mention } from '../../editor/types';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { UserType } from '../../graphql/generated/graphql';
import mentionIcon from '../../icons/non-prefilled/16x16/mention.svg';
import { miscToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import useIsPublicView from '../../utils/useIsPublicView';
import MagneticWallItem from '../MagneticWallItem';
import MagneticWallItemAvatar from '../MagneticWallItemAvatar';
import { COMMENT_THREAD_CREATION_ID } from '../PendingMagneticWallItemProvider';
import { PostPolicyAction, usePostPolicyCheck } from '../postPolicyCheck';
import isContentEmpty from '../utils/isContentEmpty';
import * as styles from './CommentCreate.module.scss';
import CommentInput from './CommentInput';
import CommentWarning from './CommentWarning';
import GroupNotificationCommentWarning from './GroupNotificationCommentWarning';
import useShouldShowFullPageSheet from './useShouldShowFullPageSheet';

export const OPTIMISTIC_COMMENT_IDS = new Set<string>();

const POST_FRAGMENT = gql(`
  fragment CommentCreate_post on RootPostFields {
    id
    ... on Post {
      ...usePostPolicyCheck_post
    }
  }  
`);

const THREAD_FRAGMENT = gql(`
  fragment CommentCreate_thread on RootThreadFields {
    id
    author {
      id
    }
  }
`);

interface CommentCreateProps {
  initialContent?: Op[];
  post: DocumentType<typeof POST_FRAGMENT>;
  thread: DocumentType<typeof THREAD_FRAGMENT>;
  onChange?: (content: Op[]) => void;
  onResize?: (avoidRepositioningFocusedThread?: boolean) => void;
  onCreated?: () => void;
  createComment: (comment: string, notifyGroups: boolean) => void;
  addComment: (args: {
    content: string;
    id: string;
    notifyGroups: boolean;
  }) => void;
  disabled: boolean;
}

function isOpWithGroupMention(op: Op): op is Op & {
  insert: { mention: Mention & { type: 'group' } };
} {
  const { insert } = op;
  if (insert != null && typeof insert !== 'string' && insert.mention != null) {
    const mention = insert.mention as Mention;
    return mention.type === 'group';
  }
  return false;
}

const CommentCreate = ({
  initialContent = [],
  post,
  thread,
  onChange,
  onResize,
  onCreated,
  disabled,
  createComment,
  addComment,
}: CommentCreateProps) => {
  const isPublicView = useIsPublicView();
  const [commentInputKey, setCommentInputKey] = useState(() => _.uniqueId());
  const [content, setContent] = useState(initialContent);
  const [isGroupNotifyingEnabled, setIsGroupNotifyingEnabled] = useState(true);

  const isNewThread = thread.id === COMMENT_THREAD_CREATION_ID;

  const mentionedGroupIds = content
    ? _.uniq(
        content.filter(isOpWithGroupMention).map((op) => op.insert.mention.id),
      )
    : [];

  const { quill } = useQuill();

  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const currentUser = useAppSelector((state) => state.session.user);

  const dispatch = useAppDispatch();

  const handleChange = (newContent: Op[]) => {
    setContent(newContent);
    onChange?.(newContent);
    onResize?.(true);
  };

  const handleSave = () => {
    if (!content.length) {
      return;
    }

    const json = JSON.stringify(content);

    try {
      if (isNewThread) {
        if (!quill) throw new Error('quill is not ready');
        createComment(
          json,

          isGroupNotifyingEnabled,
        );
      } else {
        addComment({
          content: json,
          id: thread.id,
          notifyGroups: isGroupNotifyingEnabled,
        });
      }
      handleChange([]);
      onCreated?.();
      setCommentInputKey(_.uniqueId());
      setIsGroupNotifyingEnabled(true);
    } catch (err) {
      dispatch(UIActions.pushToast(miscToasts.genericError));
    }
  };

  const shouldShowFullPageSheet = useShouldShowFullPageSheet();

  const shouldAutoFocus =
    (!shouldShowFullPageSheet || isNewThread) &&
    (readOnly || !quill?.hasFocus()) &&
    !disabled;

  const postPolicyCheck = usePostPolicyCheck();

  return (
    <div>
      <CommentInput
        key={commentInputKey}
        autoFocus={shouldAutoFocus}
        initialContent={content}
        onChange={handleChange}
        onSave={handleSave}
        disabled={disabled}
      >
        {({ input, mentionUser, insertText }) => (
          <div className={styles.container}>
            <MagneticWallItem.WithAvatar
              avatar={
                <div className={styles.avatar}>
                  <MagneticWallItemAvatar user={currentUser} />
                </div>
              }
              disabled={disabled}
            >
              <ScrollArea
                className={classNames(styles.editor, {
                  [styles.newThread]: isNewThread,
                })}
              >
                {input}
              </ScrollArea>
            </MagneticWallItem.WithAvatar>
            <div className={styles.actions}>
              <MagneticWallItem.ActionContainer>
                <MagneticWallItem.EmojiPickerButton
                  onPick={insertText}
                  disabled={disabled}
                />
                {currentUser.type === UserType.Visitor ? null : (
                  <MagneticWallItem.IconButton
                    icon={mentionIcon}
                    onClick={() => mentionUser()}
                    tooltip="Mention someone"
                    disabled={disabled}
                  />
                )}
                <MagneticWallItem.ButtonGroup>
                  <MagneticWallItem.SubmitButton
                    disabled={disabled || isContentEmpty(content)}
                    onClick={handleSave}
                  >
                    Comment
                  </MagneticWallItem.SubmitButton>
                </MagneticWallItem.ButtonGroup>
              </MagneticWallItem.ActionContainer>
            </div>
          </div>
        )}
      </CommentInput>
      {mentionedGroupIds.length > 0 && (
        <GroupNotificationCommentWarning
          groupIds={mentionedGroupIds}
          onChange={setIsGroupNotifyingEnabled}
          isNotifyingEnabled={isGroupNotifyingEnabled}
        />
      )}
      {!isPublicView &&
        post &&
        post.__typename === 'Post' &&
        !postPolicyCheck(
          PostPolicyAction.EditPost,
          post,
          thread.author ?? currentUser, // If thread does not have an author, this is a new comment thread
        ) && (
          <Tooltip
            trigger="clickOrHover"
            display="block"
            placement="bottom"
            content={
              'Comment threads authored\nby users without post edit\naccess are only visible to\nthem and post editors'
            }
          >
            <CommentWarning>This comment has limited visibility</CommentWarning>
          </Tooltip>
        )}
    </div>
  );
};

export default CommentCreate;
